import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Eye, 
  TrendingUp, 
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  MessageCircle,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAdmin } from '@/context/AdminContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { token } = useAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadChats, setUnreadChats] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/analytics/overview?token=${token}`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();

    // Check for Tawk.to unread messages
    const checkTawkMessages = () => {
      if (window.Tawk_API && window.Tawk_API.getStatus) {
        try {
          // Tawk.to API for checking status
          const status = window.Tawk_API.getStatus();
          console.log('Tawk status:', status);
        } catch (e) {
          console.log('Tawk API not ready');
        }
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkTawkMessages, 30000);
    checkTawkMessages();

    return () => clearInterval(interval);
  }, [token]);

  const statCards = [
    {
      title: 'Total Contacts',
      value: stats?.total_contacts || 0,
      subtext: `${stats?.contacts_week || 0} this week`,
      icon: Users,
      color: 'bg-blue-500',
      trend: stats?.contacts_week > 0 ? 'up' : 'neutral'
    },
    {
      title: 'Page Views',
      value: stats?.total_pageviews || 0,
      subtext: `${stats?.pageviews_today || 0} today`,
      icon: Eye,
      color: 'bg-emerald-500',
      trend: stats?.pageviews_today > 0 ? 'up' : 'neutral'
    },
    {
      title: 'Weekly Views',
      value: stats?.pageviews_week || 0,
      subtext: 'Last 7 days',
      icon: TrendingUp,
      color: 'bg-purple-500',
      trend: 'up'
    },
    {
      title: 'Monthly Views',
      value: stats?.pageviews_month || 0,
      subtext: 'Last 30 days',
      icon: Calendar,
      color: 'bg-amber-500',
      trend: 'up'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="admin-dashboard-title">Dashboard</h1>
        <p className="text-slate-400">Welcome back! Here's what's happening.</p>
      </div>

      {/* Live Chat Card - Prominent Position */}
      <Card className="bg-gradient-to-r from-teal-900/50 to-emerald-900/50 border-teal-700/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-teal-500 p-4 rounded-xl">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Live Chat (Tawk.to)</h3>
                <p className="text-teal-300 text-sm mt-1">
                  Respond to visitor chats and manage conversations
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-4">
                <p className="text-teal-300 text-sm">Chat Status</p>
                <p className="text-white font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  Active
                </p>
              </div>
              <Button 
                asChild
                className="bg-teal-600 hover:bg-teal-700"
              >
                <a href="https://dashboard.tawk.to" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Chat Dashboard
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <Card className="bg-slate-800 border-slate-700" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">{stat.title}</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {stat.value.toLocaleString()}
                    </p>
                    <p className="text-slate-500 text-sm mt-1 flex items-center gap-1">
                      {stat.trend === 'up' && <ArrowUpRight className="w-4 h-4 text-emerald-500" />}
                      {stat.trend === 'down' && <ArrowDownRight className="w-4 h-4 text-red-500" />}
                      {stat.subtext}
                    </p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white">Top Pages</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.page_stats?.length > 0 ? (
              <div className="space-y-3">
                {stats.page_stats.slice(0, 5).map((page, index) => {
                  const maxCount = Math.max(...stats.page_stats.map(p => p.count));
                  const percentage = (page.count / maxCount) * 100;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300">{page.page || '/'}</span>
                        <span className="text-white font-medium">{page.count} views</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No page view data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Quick Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <a 
              href="https://dashboard.tawk.to" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-teal-400" />
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Tawk.to Dashboard</p>
                <p className="text-slate-500 text-xs">Manage live chats</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-500" />
            </a>
            <a 
              href="https://analytics.google.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <TrendingUp className="w-5 h-5 text-blue-400" />
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Google Analytics</p>
                <p className="text-slate-500 text-xs">Advanced analytics</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-500" />
            </a>
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Eye className="w-5 h-5 text-emerald-400" />
              <div className="flex-1">
                <p className="text-white text-sm font-medium">View Live Site</p>
                <p className="text-slate-500 text-xs">See your website</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-500" />
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
